import {
  assignProjectParcel,
  getMyProjectParcels,
  getProjectParcels,
  getProjectUsers,
  unassignProjectParcel,
  updateProjectUser,
  updateProjectUserProfile,
  verifyProjectUser,
} from "helpers/apiClient";

const canonicalRoles = new Set(["admin", "tecnico", "regante"]);
const membershipChanges = new Set(["role", "status"]);

export const loadProjectUsers = async (projectId) => {
  if (!projectId) return [];
  const response = await getProjectUsers(projectId);
  return Array.isArray(response) ? response : response?.users || [];
};

export const changeProjectUserMembership = (projectId, userId, change) => {
  const canonicalUserId = projectUserId(userId);
  const keys = Object.keys(change || {});
  if (keys.length !== 1 || !membershipChanges.has(keys[0])) {
    throw new Error("A membership update must contain exactly one role or status change.");
  }
  if (keys[0] === "role" && !canonicalRoles.has(change.role)) {
    throw new Error("Invalid Project role.");
  }
  return updateProjectUser(projectId, canonicalUserId, change);
};

export const verifyProjectUserIdentity = (projectId, userId) =>
  verifyProjectUser(projectId, projectUserId(userId));

export const updateCommunityUserProfile = (projectId, userId, profile) =>
  updateProjectUserProfile(projectId, projectUserId(userId), profile);

export const loadProjectParcels = async (projectId) => {
  if (!projectId) return [];
  const response = await getProjectParcels(projectId);
  const parcels = Array.isArray(response) ? response : response?.parcels || [];
  return parcels.map(toCommunityParcel);
};

export const loadCurrentUserParcels = async (projectId) => {
  if (!projectId) return [];
  const response = await getMyProjectParcels(projectId);
  const parcels = Array.isArray(response) ? response : response?.parcels || [];
  return parcels.map(toCommunityParcel);
};

export const withCurrentUserParcels = (profile, parcels) => {
  const normalized = Array.isArray(parcels) ? parcels : [];
  return {
    ...profile,
    parcels: normalized,
    ...legacyParcelFields(normalized),
  };
};

export const assignProjectUserParcel = (projectId, userId, parcelId) =>
  assignProjectParcel(projectId, projectParcelId(parcelId), projectUserId(userId));

export const removeProjectUserParcel = (projectId, userId, parcelId) =>
  unassignProjectParcel(projectId, projectParcelId(parcelId), projectUserId(userId));

const projectUserId = (userOrId) => {
  const candidate = typeof userOrId === "object" && userOrId !== null
    ? userOrId.userId ?? userOrId.id
    : userOrId;
  const userId = Number(candidate);
  if (!Number.isSafeInteger(userId) || userId <= 0) {
    throw new Error("Invalid Project user.");
  }
  return userId;
};

const projectParcelId = (parcelOrId) => {
  const candidate = typeof parcelOrId === "object" && parcelOrId !== null
    ? parcelOrId.id ?? parcelOrId.parcelId
    : parcelOrId;
  const parcelId = String(candidate || "").trim();
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(parcelId)) {
    throw new Error("Invalid Project parcel.");
  }
  return parcelId;
};

const legacyParcelFields = (parcels) => {
  const [parcel = {}] = Array.isArray(parcels) ? parcels : [];
  return {
    parcela: parcel.irrigationReference ?? "",
    poligono: parcel.polygonReference ?? "",
    catastro: parcel.cadastralReference ?? "",
    parcela_orden: parcel.irrigationOrder ?? "",
  };
};

const toCommunityParcel = (parcel) => ({
  id: projectParcelId(parcel),
  irrigationReference: parcel.irrigationReference ?? null,
  cadastralReference: parcel.cadastralReference ?? null,
  polygonReference: parcel.polygonReference ?? null,
  irrigationOrder: parcel.irrigationOrder ?? null,
  createdAt: parcel.createdAt,
  updatedAt: parcel.updatedAt,
});

export const toCommunityUser = (member) => {
	const parcels = Array.isArray(member.parcels) ? member.parcels.map(toCommunityParcel) : [];
  const parcelFields = legacyParcelFields(parcels);
  return {
    id: member.userId,
	username: member.username || "",
    role: canonicalRoles.has(member.role) ? member.role : "guest",
    roleStatus: member.roleStatus || "",
    isVerified: Boolean(member.validated),
    isEmailVerified: Boolean(member.validated),
    email: member.email || "",
    profilePicture: member.profilePicture || "",
    ...parcelFields,
    parcels,
    projectId: member.projectId,
    contactDetails: {
		firstName: member.name || "",
		lastName: member.lastName || "",
		team: member.team || "",
		workRole: member.workRole || "",
		allowWhatsapp: Boolean(member.allowWhatsapp),
		address: {
			street: member.address || "",
			city: member.city || "",
			postalCode: member.postalCode || "",
			country: member.country || "",
		},
      email: member.email || "",
      phone: member.phone || "N/A",
      bio: "",
    },
  };
};

export const toAssignee = (member, index) => ({
  key: String(member.userId ?? `user_${index}`),
  label: member.name || member.email || "",
  role: canonicalRoles.has(member.role) ? member.role : "guest",
});
